import type { SessionCode, SessionUser, UserRole } from './auth.js';
import { USER_ROLE_LABELS } from './auth.js';

/** Hub tiles and app-menu destinations (F-10). Catalog is not a service. */

export const SERVICE_IDS = [
  'scan-qr',
  'events',
  'availability',
  'inventory',
  'assets',
  'totes',
  'bins',
  'trailers',
  'replacements',
  'users',
  'labels',
  'audit',
  'settings',
  'help',
] as const;
export type ServiceId = (typeof SERVICE_IDS)[number];

export type ServiceAudience =
  'signed-in' | 'google' | 'admin' | 'equipment-or-admin' | 'availability';

export type ServiceAction = 'route' | 'scan-qr';

export interface ServiceDefinition {
  id: ServiceId;
  label: string;
  audience: ServiceAudience;
  action: ServiceAction;
  href: string;
}

export const SERVICES: readonly ServiceDefinition[] = [
  { id: 'scan-qr', label: 'Scan QR', audience: 'signed-in', action: 'scan-qr', href: '/' },
  { id: 'events', label: 'Events', audience: 'signed-in', action: 'route', href: '/events' },
  {
    id: 'availability',
    label: 'Availability',
    audience: 'availability',
    action: 'route',
    href: '/availability',
  },
  {
    id: 'inventory',
    label: 'Inventory',
    audience: 'signed-in',
    action: 'route',
    href: '/inventory/new',
  },
  { id: 'assets', label: 'Assets', audience: 'google', action: 'route', href: '/assets' },
  { id: 'totes', label: 'Totes', audience: 'google', action: 'route', href: '/totes' },
  { id: 'bins', label: 'Bins', audience: 'google', action: 'route', href: '/bins' },
  { id: 'trailers', label: 'Loadouts', audience: 'google', action: 'route', href: '/trailers' },
  {
    id: 'replacements',
    label: 'Replacements',
    audience: 'equipment-or-admin',
    action: 'route',
    href: '/replacements',
  },
  { id: 'users', label: 'Users', audience: 'admin', action: 'route', href: '/admin/users' },
  {
    id: 'labels',
    label: 'Labels',
    audience: 'equipment-or-admin',
    action: 'route',
    href: '/admin/labels',
  },
  {
    id: 'audit',
    label: 'Audit',
    audience: 'equipment-or-admin',
    action: 'route',
    href: '/admin/audit',
  },
  {
    id: 'settings',
    label: 'Settings',
    audience: 'equipment-or-admin',
    action: 'route',
    href: '/admin/settings',
  },
  { id: 'help', label: 'Help', audience: 'signed-in', action: 'route', href: '/help' },
];

export const EVENT_CODE_ROLE_LABEL = 'Event code';

export function sessionRoleLabel(user: SessionUser | null, code: SessionCode | null): string {
  if (code) return EVENT_CODE_ROLE_LABEL;
  if (user) return USER_ROLE_LABELS[user.role];
  return '';
}

export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    const word = parts[0] ?? '';
    return word.slice(0, Math.min(2, word.length)).toUpperCase();
  }
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';
  return (first + last).toUpperCase();
}

export function canSeeService(
  service: ServiceDefinition,
  user: SessionUser | null,
  code: SessionCode | null,
): boolean {
  if (!user && !code) return false;
  switch (service.audience) {
    case 'signed-in':
      return true;
    case 'google':
      return Boolean(user);
    case 'admin':
      return user?.role === 'admin';
    case 'equipment-or-admin':
      return user?.role === 'admin' || user?.role === 'equipment_admin';
    case 'availability':
      return user?.role === 'admin' || user?.role === 'equipment_admin' || user?.role === 'staff';
    default:
      return false;
  }
}

export function visibleServices(
  user: SessionUser | null,
  code: SessionCode | null,
): ServiceDefinition[] {
  return SERVICES.filter((service) => canSeeService(service, user, code));
}

export function filterServicesByQuery(
  services: readonly ServiceDefinition[],
  query: string,
): ServiceDefinition[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...services];
  return services.filter((service) => service.label.toLowerCase().includes(needle));
}

export function serviceIsActive(pathname: string, service: ServiceDefinition): boolean {
  if (service.action === 'scan-qr') return false;
  if (service.id === 'inventory') return pathname.startsWith('/inventory');
  return pathname === service.href || pathname.startsWith(`${service.href}/`);
}

export function roleCanSearchUsers(role: UserRole): boolean {
  return role === 'admin';
}

export function roleCanSearchCatalog(user: SessionUser | null, code: SessionCode | null): boolean {
  return Boolean(user) || Boolean(code);
}
